/**
 * CRM Database Operations
 *
 * Database queries for comprehensive CRM system including companies, contacts,
 * leads, opportunities, activities, campaigns, and integrations.
 * Uses service client on server when no client passed; browser client only on client.
 */

import { createServiceClient } from "@/lib/supabase/service-client"
import { buildPostgrestIlikeOrFilter } from "@/lib/supabase/postgrest-filters"
import {
  buildCrmSearchPostgrestOrFilter,
  rankCrmSearchRecords,
  type CrmSearchValue
} from "@/lib/crm/search"
import { safeConsole } from "@/lib/utils/safeConsole"
import { safeIndexEntity } from "@/lib/knowledge-graph/safe-index"
import type { MeaningfulOutcomeProperties } from "@/lib/analytics/meaningful-outcome-contract"
import { SupabaseClient } from "@supabase/supabase-js"
import { Database } from "@/supabase/types"
import {
  CRMCompany,
  CRMContact,
  CRMLead,
  CRMOpportunity,
  CRMOpportunityProduct,
  CRMActivity,
  CRMActivityAttachment,
  CRMCampaign,
  CRMCampaignRecipient,
  CRMLeadScoringProfile,
  CRMLeadScoringRule,
  CRMAutomationRule,
  CRMAutomationExecution,
  CRMIntegration,
  CRMSyncLog,
  CRMAgentConfig,
  CRMLeadDashboard,
  CRMPipelineItem,
  CRMCampaignPerformance,
  CRMContactStatus,
  CRMOutboundMessage,
  CRMOutboundChannel,
  CRMOutboundStatus,
  CRMScheduledAction,
  CRMActivityType,
  CRMPriority,
  CRMScheduledActionStatus,
  NexusAgent
} from "@/types/nexus"

/** Use service client on server when no client passed; browser client only on client. */
function getDefaultClient(passed?: SupabaseClient<Database>): SupabaseClient<Database> {
  if (passed) return passed
  if (typeof window === "undefined") return createServiceClient()
  return require("@/lib/supabase/browser-client").supabase as SupabaseClient<Database>
}

/** Typed client for table/RPC inference. */
function getClient(passed?: SupabaseClient<Database>): SupabaseClient<Database> {
  return getDefaultClient(passed) as SupabaseClient<Database>
}

function trackBrowserCrmOutcome(properties: MeaningfulOutcomeProperties) {
  if (typeof window === "undefined") return

  void import("@/lib/analytics/meaningful-outcome")
    .then(({ trackMeaningfulOutcomeCompleted }) =>
      trackMeaningfulOutcomeCompleted(properties)
    )
    .catch(() => {
      // Analytics should never block CRM actions.
    })
}

type CRMJsonRecord = Record<string, unknown>

type CRMProfileSummary = {
  user_id: string | null
  username?: string | null
  display_name?: string | null
  image_url?: string | null
}

type CRMActivitySourceRow = CRMJsonRecord & {
  activity_type?: string | null
  type?: string | null
  performed_by?: string | null
  performed_by_type?: string | null
}

type CRMCountQueryResult = {
  count?: number | null
  data?: CRMJsonRecord[] | null
  error: unknown
}

const CRM_STATS_TABLES = [
  { key: "companies", table: "crm_companies" },
  { key: "contacts", table: "crm_contacts" },
  { key: "leads", table: "crm_contacts" },
  { key: "opportunities", table: "crm_opportunities" },
  { key: "activities", table: "crm_activities" },
  { key: "campaigns", table: "crm_campaigns" },
  { key: "forms", table: "crm_forms" }
] as const

type CRMStatsKey = (typeof CRM_STATS_TABLES)[number]["key"]
type CRMStats = Record<CRMStatsKey, number>

function createEmptyCRMStats(): CRMStats {
  return {
    companies: 0,
    contacts: 0,
    leads: 0,
    opportunities: 0,
    activities: 0,
    campaigns: 0,
    forms: 0
  }
}

function getCrmSearchCandidateLimit(limit: number, offset: number) {
  return Math.min(Math.max(offset + limit * 8, 100), 500)
}

function getContactSearchValues(contact: CRMContact): CrmSearchValue[] {
  return [
    `${contact.first_name} ${contact.last_name}`,
    contact.first_name,
    contact.last_name,
    contact.email,
    contact.phone,
    contact.mobile,
    contact.company,
    contact.title,
    contact.department,
    contact.group_name
  ]
}

function getLeadSearchValues(lead: CRMLead): CrmSearchValue[] {
  return [
    `${lead.first_name} ${lead.last_name}`,
    lead.first_name,
    lead.last_name,
    lead.email,
    lead.phone,
    lead.mobile,
    lead.company,
    lead.title,
    lead.industry,
    lead.website,
    lead.source_details,
    lead.group_name,
    lead.notes
  ]
}

type CRMDynamicQuery = PromiseLike<CRMCountQueryResult> & {
  eq: (column: string, value: string) => CRMDynamicQuery
}

const CRM_SEARCH_FALLBACK_LIMIT = 200
const CRM_CONTACT_SEARCH_COLUMNS = [
  "first_name",
  "last_name",
  "email",
  "phone",
  "mobile",
  "company",
  "title",
  "department"
]
const CRM_LEAD_SEARCH_COLUMNS = [
  "first_name",
  "last_name",
  "email",
  "phone",
  "mobile",
  "company",
  "title",
  "industry",
  "website",
  "source_details",
  "notes",
  "group_name"
]

type CRMDynamicTableClient = {
  from: (tableName: string) => {
    select: (
      columns: string,
      options?: { count?: "exact"; head?: boolean }
    ) => CRMDynamicQuery
  }
}

const DEFAULT_CAMPAIGN_METRICS: CRMCampaign["metrics"] = {
  sent: 0,
  delivered: 0,
  opened: 0,
  clicked: 0,
  replied: 0,
  bounced: 0,
  unsubscribed: 0,
  converted: 0
}

function normalizeCampaignMetrics(metrics: unknown): CRMCampaign["metrics"] {
  if (!metrics || typeof metrics !== "object" || Array.isArray(metrics)) {
    return { ...DEFAULT_CAMPAIGN_METRICS }
  }

  const record = metrics as CRMJsonRecord

  return {
    sent: Number(record.sent ?? 0),
    delivered: Number(record.delivered ?? 0),
    opened: Number(record.opened ?? 0),
    clicked: Number(record.clicked ?? 0),
    replied: Number(record.replied ?? 0),
    bounced: Number(record.bounced ?? 0),
    unsubscribed: Number(record.unsubscribed ?? 0),
    converted: Number(record.converted ?? 0)
  }
}

function normalizeCampaign(row: unknown): CRMCampaign {
  const record = row as CRMJsonRecord

  return {
    ...record,
    metrics: normalizeCampaignMetrics(record.metrics)
  } as unknown as CRMCampaign
}

function normalizeCampaignPerformanceRows(rows: unknown): CRMCampaignPerformance[] {
  if (!Array.isArray(rows)) return []

  return rows.map(row => {
    const record = row as CRMJsonRecord
    const metrics = normalizeCampaignMetrics(record.metrics)

    return {
      ...record,
      metrics,
      replied_count: Number(record.replied_count ?? metrics.replied),
      bounced_count: Number(record.bounced_count ?? metrics.bounced),
      unsubscribed_count: Number(record.unsubscribed_count ?? metrics.unsubscribed)
    } as unknown as CRMCampaignPerformance
  })
}

function getActivityPerformerUserIds(activities: readonly CRMActivitySourceRow[]): string[] {
  return [...new Set(
    activities.flatMap(activity => (
      typeof activity.performed_by === "string" &&
      activity.performed_by_type === "user"
        ? [activity.performed_by]
        : []
    ))
  )]
}

function withActivityProfiles(
  activities: readonly CRMActivitySourceRow[],
  profilesData: readonly CRMProfileSummary[]
): CRMActivity[] {
  return activities.map(activity => ({
    ...activity,
    activity_type: activity.activity_type ?? activity.type,
    performed_by_profile: profilesData.find(p => p.user_id === activity.performed_by) || null
  })) as unknown as CRMActivity[]
}

// ------------------------------------------------------------------------------
// COMPANIES
// ------------------------------------------------------------------------------

export async function createCompany(
  company: Omit<CRMCompany, "id" | "created_at" | "updated_at">
): Promise<CRMCompany> {
  const client = getClient()
  const { data, error } = await client
    .from("crm_companies")
    .insert(company)
    .select()
    .single()

  if (error) throw error
  const createdCompany = data as CRMCompany
  // Index-on-write into the knowledge graph (server-only, non-blocking).
  if (typeof window === "undefined") {
    void safeIndexEntity(
      "company",
      createdCompany as unknown as Record<string, unknown>,
      createdCompany.workspace_id,
      client
    )
  }
  return createdCompany
}

export async function getCompaniesByWorkspace(
  workspaceId: string,
  limit = 50,
  offset = 0
): Promise<CRMCompany[]> {
  const { data, error } = await getClient()
    .from("crm_companies")
    .select("*")
    .eq("workspace_id", workspaceId)
    .order("created_at", { ascending: false })
    .range(offset, offset + limit - 1)

  if (error) throw error
  return data as CRMCompany[]
}

export async function getCompanyById(companyId: string): Promise<CRMCompany> {
  const { data, error } = await getClient()
    .from("crm_companies")
    .select("*")
    .eq("id", companyId)
    .single()

  if (error) throw error
  return data as CRMCompany
}

export async function updateCompany(
  companyId: string,
  updates: Partial<CRMCompany>
): Promise<CRMCompany> {
  const { data, error } = await getClient()
    .from("crm_companies")
    .update(updates)
    .eq("id", companyId)
    .select()
    .single()

  if (error) throw error
  return data as CRMCompany
}

export async function deleteCompany(companyId: string): Promise<void> {
  const { error } = await getClient()
    .from("crm_companies")
    .delete()
    .eq("id", companyId)

  if (error) throw error
}

export async function searchCompanies(
  workspaceId: string,
  query: string,
  limit = 20,
  offset = 0
): Promise<CRMCompany[]> {
  const { data, error } = await getClient()
    .from("crm_companies")
    .select("*")
    .eq("workspace_id", workspaceId)
    .or(buildPostgrestIlikeOrFilter(["name", "domain", "industry"], query))
    .order("name")
    .range(offset, offset + limit - 1)

  if (error) throw error
  return data as CRMCompany[]
}

// ------------------------------------------------------------------------------
// CONTACTS
// ------------------------------------------------------------------------------

export async function createContact(
  contact: Omit<CRMContact, "id" | "created_at" | "updated_at">
): Promise<CRMContact> {
  const client = getClient()
  const { data, error } = await client
    .from("crm_contacts")
    .insert(contact)
    .select()
    .single()

  if (error) throw error
  const createdContact = data as CRMContact

  // Index-on-write into the knowledge graph (server-only, non-blocking).
  if (typeof window === "undefined") {
    void safeIndexEntity(
      "contact",
      createdContact as unknown as Record<string, unknown>,
      createdContact.workspace_id,
      client
    )
  }

  return createdContact
}

export async function getContactsByWorkspace(
  workspaceId: string,
  limit = 50,
  offset = 0
): Promise<CRMContact[]> {
  const { data, error } = await getClient()
    .from("crm_contacts")
    .select("*")
    .eq("workspace_id", workspaceId)
    .order("created_at", { ascending: false })
    .range(offset, offset + limit - 1)

  if (error) throw error
  return data as CRMContact[]
}

export async function getContactById(contactId: string): Promise<CRMContact> {
  const { data, error } = await getClient()
    .from("crm_contacts")
    .select("*")
    .eq("id", contactId)
    .single()

  if (error) throw error
  return data as CRMContact
}

export async function updateContact(
  contactId: string,
  updates: Partial<CRMContact>
): Promise<CRMContact> {
  const { data, error } = await getClient()
    .from("crm_contacts")
    .update(updates)
    .eq("id", contactId)
    .select()
    .single()

  if (error) throw error
  return data as CRMContact
}

export async function deleteContact(contactId: string): Promise<void> {
  const { error } = await getClient()
    .from("crm_contacts")
    .delete()
    .eq("id", contactId)

  if (error) throw error
}

export async function searchContacts(
  workspaceId: string,
  query: string,
  limit = 20,
  offset = 0
): Promise<CRMContact[]> {
  if (!query.trim()) return []

  const candidateLimit = getCrmSearchCandidateLimit(limit, offset)
  const { data, error } = await getClient()
    .from("crm_contacts")
    .select("*")
    .eq("workspace_id", workspaceId)
    .or(buildCrmSearchPostgrestOrFilter(CRM_CONTACT_SEARCH_COLUMNS, query))
    .range(0, candidateLimit - 1)

  if (error) throw error

  const rankedContacts = rankCrmSearchRecords(
    (data ?? []) as CRMContact[],
    query,
    getContactSearchValues
  )

  if (rankedContacts.length > 0) {
    return rankedContacts.slice(offset, offset + limit)
  }

  const fallback = await getClient()
    .from("crm_contacts")
    .select("*")
    .eq("workspace_id", workspaceId)
    .order("updated_at", { ascending: false })
    .limit(CRM_SEARCH_FALLBACK_LIMIT)

  if (fallback.error) throw fallback.error

  return rankCrmSearchRecords(
    (fallback.data ?? []) as CRMContact[],
    query,
    getContactSearchValues
  ).slice(offset, offset + limit)
}

// ------------------------------------------------------------------------------
// LEADS
// ------------------------------------------------------------------------------

export async function createLead(
  lead: Omit<CRMLead, "id" | "created_at" | "updated_at">,
  supabaseClient?: SupabaseClient<Database>
): Promise<CRMLead> {
  const client = getClient(supabaseClient)
  const { data, error } = await client
    .from("crm_contacts")
    .insert(lead)
    .select()
    .single()

  if (error) throw error
  const createdLead = data as CRMLead

  // Index-on-write into the knowledge graph (server-only, non-blocking).
  if (typeof window === "undefined") {
    void safeIndexEntity("lead", createdLead, createdLead.workspace_id, client)
  }

  // Trigger nurturing workflows for new lead via API (async, don't wait)
  // This avoids bundling server-only modules in client code
  if (data && lead.workspace_id && typeof window === 'undefined') {
    // Use fetch to trigger workflows via API to avoid webpack bundling issues
    const baseUrl = process.env.NEXT_PUBLIC_APP_URL || 'http://localhost:3000'
    const internalKey = process.env.NEXUS_SERVER_EVAL_KEY || process.env.LOSI_INTERNAL_API_KEY
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }

    if (internalKey) {
      headers['x-losi-internal-key'] = internalKey
    }

    fetch(`${baseUrl}/api/crm/nurturing/auto-trigger`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        lead_id: data.id,
        workspace_id: lead.workspace_id
      })
    }).catch(() => {
      // Silently fail - workflow triggering is not critical
    })
  }

  trackBrowserCrmOutcome({
    surface: "nexus",
    outcome_type: "crm_lead",
    qualifying_action: "created",
    lead_id: createdLead.id,
    workspace_id: createdLead.workspace_id
  })

  return createdLead
}

export async function getLeadsByWorkspace(
  workspaceId: string,
  limit = 50,
  offset = 0,
  supabaseClient?: SupabaseClient<Database>
): Promise<CRMLead[]> {
  const client = getClient(supabaseClient)
  const { data, error } = await client
    .from("crm_contacts")
    .select("*")
    .eq("workspace_id", workspaceId)
    .order("created_at", { ascending: false })
    .range(offset, offset + limit - 1)

  if (error) throw error
  return data as CRMLead[]
}

export async function getProcessedLeadById(leadId: string, supabaseClient?: any): Promise<CRMLead | null> {
  const client = getClient(supabaseClient)
  const { data, error } = await client
    .from("crm_contacts")
    .select("*")
    .eq("id", leadId)
    .single()

  // Handle PGRST116 error (no rows returned) gracefully
  if (error && error.code === 'PGRST116') {
    return null
  }

  if (error) throw error
  return data as CRMLead
}

export async function getLeadById(leadId: string, supabaseClient?: any): Promise<CRMLead | null> {
  const client = getClient(supabaseClient)
  const { data, error } = await client
    .from("crm_contacts")
    .select("*")
    .eq("id", leadId)
    .single()

  // Handle PGRST116 error (no rows returned) gracefully
  if (error && error.code === 'PGRST116') {
    return null
  }

  if (error) throw error
  return data as CRMLead
}

export async function getLeadCaptureById(leadId: string, supabaseClient?: any): Promise<CRMLead | null> {
  const client = getClient(supabaseClient)
  const { data, error } = await client
    .from("crm_contacts")
    .select("*")
    .eq("id", leadId)
    .single()

  // Handle PGRST116 error (no rows returned) gracefully
  if (error && error.code === 'PGRST116') {
    return null
  }

  if (error) throw error
  return data as CRMLead
}

export async function updateLead(
  leadId: string,
  updates: Partial<CRMLead>,
  supabaseClient?: SupabaseClient<Database>
): Promise<CRMLead> {
  const client = getClient(supabaseClient)
  const { data, error } = await client
    .from("crm_contacts")
    .update(updates)
    .eq("id", leadId)
    .select()
    .single()

  if (error) throw error
  return data as CRMLead
}

export async function deleteLead(leadId: string): Promise<void> {
  const { error } = await getClient()
    .from("crm_contacts")
    .delete()
    .eq("id", leadId)

  if (error) throw error
}

export async function searchLeads(
  workspaceId: string,
  query: string,
  limit = 20,
  offset = 0
): Promise<CRMLead[]> {
  if (!query.trim()) return []

  return searchLeadRows(workspaceId, query, limit, offset)
}

async function searchLeadRows(
  workspaceId: string,
  query: string,
  limit: number,
  offset: number
): Promise<CRMLead[]> {
  const candidateLimit = getCrmSearchCandidateLimit(limit, offset)
  const { data, error } = await getClient()
    .from("crm_contacts")
    .select("*")
    .eq("workspace_id", workspaceId)
    .or(buildCrmSearchPostgrestOrFilter(CRM_LEAD_SEARCH_COLUMNS, query))
    .range(0, candidateLimit - 1)

  if (error) throw error

  const rankedLeads = rankCrmSearchRecords(
    (data ?? []) as CRMLead[],
    query,
    getLeadSearchValues
  )

  if (rankedLeads.length > 0) {
    return rankedLeads.slice(offset, offset + limit)
  }

  const fallback = await getClient()
    .from("crm_contacts")
    .select("*")
    .eq("workspace_id", workspaceId)
    .order("updated_at", { ascending: false })
    .limit(CRM_SEARCH_FALLBACK_LIMIT)

  if (fallback.error) throw fallback.error

  return rankCrmSearchRecords(
    (fallback.data ?? []) as CRMLead[],
    query,
    getLeadSearchValues
  ).slice(offset, offset + limit)
}

export async function searchLeadCaptures(
  workspaceId: string,
  query: string,
  limit = 20,
  offset = 0
): Promise<CRMLead[]> {
  if (!query.trim()) return []

  return searchLeadRows(workspaceId, query, limit, offset)
}

export async function getLeadsByStatus(
  workspaceId: string,
  status: string,
  limit = 50
): Promise<CRMLead[]> {
  const { data, error } = await getClient()
    .from("crm_contacts")
    .select("*")
    .eq("workspace_id", workspaceId)
    .eq("status", status)
    .order("created_at", { ascending: false })
    .limit(limit)

  if (error) throw error
  return data as CRMLead[]
}

// Normalize phone number to consistent format for comparison
// Removes all non-digit characters and handles country codes
export function normalizePhoneNumber(phone: string): string {
  if (!phone || typeof phone !== 'string') return ''

  // Remove all non-digit characters
  let normalized = phone.replace(/\D/g, '')

  // Handle country codes more robustly
  // Remove leading '1' for US/Canada numbers (11 digits starting with 1)
  if (normalized.length === 11 && normalized.startsWith('1')) {
    normalized = normalized.substring(1)
  }

  // For international numbers (more than 10 digits), keep as-is
  // For US numbers, ensure we have exactly 10 digits
  if (normalized.length > 10) {
    // This could be an international number or a number with extra digits
    // Keep the last 10 digits for US numbers, or full number for international
    const last10 = normalized.slice(-10)
    const beforeLast10 = normalized.slice(0, -10)

    // If the digits before the last 10 look like a country code (1-4 digits),
    // assume it's international and keep full number
    if (beforeLast10.length >= 1 && beforeLast10.length <= 4 && !beforeLast10.startsWith('1')) {
      return normalized // International number
    } else {
      return last10 // Likely US number with extra digits
    }
  }

  return normalized
}

export async function getLeadByPhoneFromAllWorkspaces(
  userId: string,
  phone: string,
  supabaseClient?: SupabaseClient<Database>
): Promise<CRMLead | null> {
  if (!phone) return null

  const client = getClient(supabaseClient)

  // First get all workspaces the user has access to
  const { data: workspaces, error: wsError } = await client
    .from('workspaces')
    .select('id')
    .eq('user_id', userId)

  if (wsError) {
    safeConsole.error('Error fetching user workspaces:', wsError)
    return null
  }

  // Also get workspaces where user is a member
  const { data: memberWorkspaces, error: memberError } = await client
    .from('workspace_members')
    .select('workspace_id')
    .eq('user_id', userId)

  if (memberError) {
    safeConsole.error('Error fetching member workspaces:', memberError)
    return null
  }

  const workspaceIds = [
    ...workspaces.map(w => w.id),
    ...memberWorkspaces.map(m => m.workspace_id)
  ].filter((id, index, arr) => arr.indexOf(id) === index)

  if (workspaceIds.length === 0) {
    return null
  }

  // Normalize the input phone number
  const normalizedPhone = normalizePhoneNumber(phone)
  if (!normalizedPhone || normalizedPhone.length < 10) return null

  // Get last 10 digits for broad matching
  const last10Digits = normalizedPhone.slice(-10)

  // Build OR query to match any phone format containing the last 10 digits
  const orConditions: string[] = []
  orConditions.push(`phone.ilike.%${last10Digits}%`)
  orConditions.push(`mobile.ilike.%${last10Digits}%`)

  // Fetch potential matches
  const { data, error } = await client
    .from("crm_contacts")
    .select("*")
    .in("workspace_id", workspaceIds)
    .or(orConditions.join(','))
    .limit(50) // Limit results for performance

  // Handle PGRST116 error (no rows returned) gracefully
  if (error && error.code === 'PGRST116') {
    return null
  }

  if (error) {
    safeConsole.error('Error fetching leads by phone:', error)
    return null
  }

  if (!data || data.length === 0) return null

  // Normalize and compare phone numbers to find exact match
  // This ensures we match regardless of formatting differences
  for (const lead of data) {
    const leadPhone = normalizePhoneNumber(lead.phone || '')
    const leadMobile = normalizePhoneNumber(lead.mobile || '')
    
    // Match if normalized numbers are equal
    if (leadPhone === normalizedPhone || leadMobile === normalizedPhone) {
      return lead as CRMLead
    }
    
    // Also check last 10 digits match (handles country code differences)
    if (normalizedPhone.length >= 10) {
      const normalizedLast10 = normalizedPhone.slice(-10)
      if (leadPhone.slice(-10) === normalizedLast10 || leadMobile.slice(-10) === normalizedLast10) {
        return lead as CRMLead
      }
    }
  }

  return null
}

export async function getLeadByPhone(
  workspaceId: string,
  phone: string,
  supabaseClient?: SupabaseClient<Database>
): Promise<CRMLead | null> {
  if (!phone) return null
  
  const client = getClient(supabaseClient)
  
  // Normalize the input phone number
  const normalizedPhone = normalizePhoneNumber(phone)
  if (!normalizedPhone || normalizedPhone.length < 10) return null
  
  // Try multiple query patterns to match different phone formats
  // Query 1: Match with normalized digits (last 10 digits)
  const last10Digits = normalizedPhone.slice(-10)
  
  // Query 2: Match with full normalized number
  const fullNormalized = normalizedPhone
  
  // Query 3: Match with country code prefix (if we removed it)
  const withCountryCode = normalizedPhone.length === 10 ? `1${normalizedPhone}` : null
  
  // Build OR query to match any of these patterns in phone or mobile fields
  const orConditions: string[] = []
  
  // Match last 10 digits (handles formatting differences)
  orConditions.push(`phone.ilike.%${last10Digits}%`)
  orConditions.push(`mobile.ilike.%${last10Digits}%`)
  
  // Match full normalized number
  if (fullNormalized.length > 10) {
    orConditions.push(`phone.ilike.%${fullNormalized}%`)
    orConditions.push(`mobile.ilike.%${fullNormalized}%`)
  }
  
  // Match with country code if applicable
  if (withCountryCode) {
    orConditions.push(`phone.ilike.%${withCountryCode}%`)
    orConditions.push(`mobile.ilike.%${withCountryCode}%`)
  }
  
  const { data, error } = await client
    .from("crm_contacts")
    .select("*")
    .eq("workspace_id", workspaceId)
    .or(orConditions.join(','))
    .limit(50) // Limit results for performance
  
  // Handle PGRST116 error (no rows returned) gracefully
  if (error && error.code === 'PGRST116') {
    return null
  }
  
  if (error) throw error
  
  if (!data || data.length === 0) return null
  
  // Normalize and compare phone numbers to find exact match
  // This ensures we match regardless of formatting differences
  for (const lead of data) {
    const leadPhone = normalizePhoneNumber(lead.phone || '')
    const leadMobile = normalizePhoneNumber(lead.mobile || '')
    
    // Match if normalized numbers are equal
    if (leadPhone === normalizedPhone || leadMobile === normalizedPhone) {
      return lead as CRMLead
    }
    
    // Also check last 10 digits match (handles country code differences)
    if (normalizedPhone.length >= 10) {
      const normalizedLast10 = normalizedPhone.slice(-10)
      if (leadPhone.slice(-10) === normalizedLast10 || leadMobile.slice(-10) === normalizedLast10) {
        return lead as CRMLead
      }
    }
  }
  
  return null
}

export async function getContactByPhone(
  workspaceId: string,
  phone: string,
  supabaseClient?: SupabaseClient<Database>
): Promise<CRMContact | null> {
  if (!phone) return null
  
  const client = getClient(supabaseClient)
  
  // Normalize the input phone number
  const normalizedPhone = normalizePhoneNumber(phone)
  if (!normalizedPhone || normalizedPhone.length < 10) return null
  
  // Get last 10 digits for broad matching
  const last10Digits = normalizedPhone.slice(-10)
  
  // Build OR query to match any of these patterns in phone or mobile fields
  const orConditions: string[] = []
  orConditions.push(`phone.ilike.%${last10Digits}%`)
  orConditions.push(`mobile.ilike.%${last10Digits}%`)
  
  const { data, error } = await client
    .from("crm_contacts")
    .select("*")
    .eq("workspace_id", workspaceId)
    .or(orConditions.join(','))
    .limit(50) // Limit results for performance
  
  // Handle PGRST116 error (no rows returned) gracefully
  if (error && error.code === 'PGRST116') {
    return null
  }
  
  if (error) throw error
  
  if (!data || data.length === 0) return null
  
  // Normalize and compare phone numbers to find exact match
  // This ensures we match regardless of formatting differences
  for (const contact of data) {
    const contactPhone = normalizePhoneNumber(contact.phone || '')
    const contactMobile = normalizePhoneNumber(contact.mobile || '')
    
    // Match if normalized numbers are equal
    if (contactPhone === normalizedPhone || contactMobile === normalizedPhone) {
      return contact as CRMContact
    }
    
    // Also check last 10 digits match (handles country code differences)
    if (normalizedPhone.length >= 10) {
      const normalizedLast10 = normalizedPhone.slice(-10)
      if (contactPhone.slice(-10) === normalizedLast10 || contactMobile.slice(-10) === normalizedLast10) {
        return contact as CRMContact
      }
    }
  }
  
  return null
}

// ------------------------------------------------------------------------------
// OPPORTUNITIES
// ------------------------------------------------------------------------------

export async function createOpportunity(
  opportunity: Omit<CRMOpportunity, "id" | "created_at" | "updated_at">
): Promise<CRMOpportunity> {
  const { data, error } = await getClient()
    .from("crm_opportunities")
    .insert(opportunity)
    .select()
    .single()

  if (error) throw error
  return data as CRMOpportunity
}

export async function getOpportunitiesByWorkspace(
  workspaceId: string,
  limit = 50,
  offset = 0
): Promise<CRMOpportunity[]> {
  const { data, error } = await getClient()
    .from("crm_opportunities")
    .select(`
      *,
      company:crm_companies!crm_opportunities_company_id_fkey (
        id,
        name
      )
    `)
    .eq("workspace_id", workspaceId)
    .order("created_at", { ascending: false })
    .range(offset, offset + limit - 1)

  if (error) throw error
  return data as CRMOpportunity[]
}

export async function getOpportunityById(opportunityId: string): Promise<CRMOpportunity> {
  const { data, error } = await getClient()
    .from("crm_opportunities")
    .select("*")
    .eq("id", opportunityId)
    .single()

  if (error) throw error
  return data as CRMOpportunity
}

export async function updateOpportunity(
  opportunityId: string,
  updates: Partial<CRMOpportunity>
): Promise<CRMOpportunity> {
  const { data, error } = await getClient()
    .from("crm_opportunities")
    .update(updates)
    .eq("id", opportunityId)
    .select()
    .single()

  if (error) throw error
  return data as CRMOpportunity
}

export async function deleteOpportunity(opportunityId: string): Promise<void> {
  const { error } = await getClient()
    .from("crm_opportunities")
    .delete()
    .eq("id", opportunityId)

  if (error) throw error
}

export async function getOpportunityProducts(opportunityId: string): Promise<CRMOpportunityProduct[]> {
  const { data, error } = await getClient()
    .from("crm_opportunity_products")
    .select("*")
    .eq("opportunity_id", opportunityId)
    .order("created_at", { ascending: true })

  if (error) throw error
  return data as CRMOpportunityProduct[]
}

export async function createOpportunityProduct(
  product: Omit<CRMOpportunityProduct, "id" | "created_at">
): Promise<CRMOpportunityProduct> {
  const { data, error } = await getClient()
    .from("crm_opportunity_products")
    .insert(product)
    .select()
    .single()

  if (error) throw error
  return data as CRMOpportunityProduct
}

// ------------------------------------------------------------------------------
// ACTIVITIES
// ------------------------------------------------------------------------------

export async function createActivity(
  activity: Omit<CRMActivity, "id" | "created_at" | "updated_at">,
  supabaseClient?: SupabaseClient<Database>
): Promise<CRMActivity> {
  const client = getClient(supabaseClient)

  // Backward compat: lead_id was dropped after crm_leads merge into crm_contacts.
  // Map any remaining lead_id references to contact_id.
  // Strip activity_type — callers pass it but the DB column is just `type`.
  const { lead_id, activity_type, ...rest } = activity as any
  const insertPayload = {
    ...rest,
    type: rest.type || activity_type || 'note',
    contact_id: rest.contact_id || lead_id || null
  }

  const { data, error } = await client
    .from("crm_activities")
    .insert(insertPayload)
    .select()
    .single()

  if (error) throw error
  const createdActivity = data as CRMActivity
  const qualifyingAction =
    createdActivity.status === "scheduled" || createdActivity.scheduled_at
      ? "scheduled"
      : "logged"

  trackBrowserCrmOutcome({
    surface: "nexus",
    outcome_type: "crm_activity",
    qualifying_action: qualifyingAction,
    activity_id: createdActivity.id,
    activity_type: createdActivity.type ?? createdActivity.activity_type,
    company_id: createdActivity.company_id ?? undefined,
    contact_id: createdActivity.contact_id ?? undefined,
    workspace_id: createdActivity.workspace_id
  })

  return createdActivity
}


export async function getActivityById(activityId: string): Promise<CRMActivity> {
  const { data, error } = await getClient()
    .from("crm_activities")
    .select("*")
    .eq("id", activityId)
    .single()

  if (error) throw error
  return data as CRMActivity
}

export async function updateActivity(
  activityId: string,
  updates: Partial<CRMActivity>
): Promise<CRMActivity> {
  const { data, error } = await getClient()
    .from("crm_activities")
    .update(updates)
    .eq("id", activityId)
    .select()
    .single()

  if (error) throw error
  return data as CRMActivity
}

export async function deleteActivity(activityId: string): Promise<void> {
  const { error } = await getClient()
    .from("crm_activities")
    .delete()
    .eq("id", activityId)

  if (error) throw error
}



export async function createActivityAttachment(
  attachment: Omit<CRMActivityAttachment, "id" | "created_at">
): Promise<CRMActivityAttachment> {
  const { data, error } = await getClient()
    .from("crm_activity_attachments")
    .insert(attachment)
    .select()
    .single()

  if (error) throw error
  return data as CRMActivityAttachment
}

// ------------------------------------------------------------------------------
// CAMPAIGNS
// ------------------------------------------------------------------------------

export async function createCampaign(
  campaign: Omit<CRMCampaign, "id" | "created_at" | "updated_at">
): Promise<CRMCampaign> {
  const { data, error } = await getClient()
    .from("crm_campaigns")
    .insert(campaign)
    .select()
    .single()

  if (error) throw error
  return normalizeCampaign(data)
}

export async function getCampaignsByWorkspace(
  workspaceId: string,
  limit = 50,
  offset = 0
): Promise<CRMCampaign[]> {
  const { data, error } = await getClient()
    .from("crm_campaigns")
    .select("*")
    .eq("workspace_id", workspaceId)
    .order("created_at", { ascending: false })
    .range(offset, offset + limit - 1)

  if (error) throw error
  return (data ?? []).map(normalizeCampaign)
}

export async function getCampaignById(campaignId: string): Promise<CRMCampaign> {
  const { data, error } = await getClient()
    .from("crm_campaigns")
    .select("*")
    .eq("id", campaignId)
    .single()

  if (error) throw error
  return normalizeCampaign(data)
}

export async function updateCampaign(
  campaignId: string,
  updates: Partial<CRMCampaign>
): Promise<CRMCampaign> {
  const { data, error } = await getClient()
    .from("crm_campaigns")
    .update(updates)
    .eq("id", campaignId)
    .select()
    .single()

  if (error) throw error
  return normalizeCampaign(data)
}

export async function deleteCampaign(campaignId: string): Promise<void> {
  const { error } = await getClient()
    .from("crm_campaigns")
    .delete()
    .eq("id", campaignId)

  if (error) throw error
}

export async function createCampaignRecipient(
  recipient: Omit<CRMCampaignRecipient, "id" | "created_at" | "updated_at">
): Promise<CRMCampaignRecipient> {
  const { data, error } = await getClient()
    .from("crm_campaign_recipients")
    .insert(recipient)
    .select()
    .single()

  if (error) throw error
  return data as CRMCampaignRecipient
}

export async function getCampaignRecipients(campaignId: string): Promise<CRMCampaignRecipient[]> {
  const { data, error } = await getClient()
    .from("crm_campaign_recipients")
    .select("*")
    .eq("campaign_id", campaignId)
    .order("created_at", { ascending: true })

  if (error) throw error
  return data as CRMCampaignRecipient[]
}

// ------------------------------------------------------------------------------
// DASHBOARD VIEWS
// ------------------------------------------------------------------------------

export async function getLeadDashboard(
  workspaceId: string,
  limit = 50,
  offset = 0
): Promise<CRMLeadDashboard[]> {
  const { data, error } = await getClient()
    .rpc('get_crm_lead_dashboard', {
      workspace_id_param: workspaceId,
      limit_param: limit,
      offset_param: offset
    })

  if (error) throw error
  return data as CRMLeadDashboard[]
}

export async function getPipeline(
  workspaceId: string,
  limit = 50,
  offset = 0
): Promise<CRMPipelineItem[]> {
  const { data, error } = await getClient()
    .rpc('get_crm_pipeline', {
      workspace_id_param: workspaceId,
      limit_param: limit,
      offset_param: offset
    })

  if (error) throw error
  return data as CRMPipelineItem[]
}

export async function getPipelineFromAllWorkspaces(
  userId: string,
  limit = 50,
  offset = 0
): Promise<CRMPipelineItem[]> {
  // First get all workspaces the user has access to
  const { data: workspaces, error: wsError } = await getClient()
    .from('workspaces')
    .select('id')
    .eq('user_id', userId)

  if (wsError) {
    safeConsole.error('Error fetching user workspaces:', wsError)
    return []
  }

  // Also get workspaces where user is a member
  const { data: memberWorkspaces, error: memberError } = await getClient()
    .from('workspace_members')
    .select('workspace_id')
    .eq('user_id', userId)

  if (memberError) {
    safeConsole.error('Error fetching member workspaces:', memberError)
    return []
  }

  const workspaceIds = [
    ...workspaces.map(w => w.id),
    ...memberWorkspaces.map(m => m.workspace_id)
  ].filter((id, index, arr) => arr.indexOf(id) === index)

  if (workspaceIds.length === 0) {
    return []
  }

  // For cross-workspace pipeline, we'll combine results from all workspaces
  const allResults: CRMPipelineItem[] = []

  for (const wsId of workspaceIds) {
    try {
      const { data } = await getClient()
        .rpc('get_crm_pipeline', {
          workspace_id_param: wsId,
          limit_param: limit,
          offset_param: offset
        })

      if (data) {
        // Add workspace info to each item
        const itemsWithWorkspace = data.map((item: any) => ({
          ...item,
          workspace_id: wsId
        }))
        allResults.push(...itemsWithWorkspace)
      }
    } catch (error) {
      safeConsole.error(`Error fetching pipeline for workspace ${wsId}:`, error)
    }
  }

  // Sort by created date and limit results
  return allResults
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(offset, offset + limit)
}

export async function getCampaignPerformance(
  workspaceId: string,
  limit = 50,
  offset = 0
): Promise<CRMCampaignPerformance[]> {
  const { data, error } = await getClient()
    .rpc('get_crm_campaign_performance', {
      workspace_id_param: workspaceId,
      limit_param: limit,
      offset_param: offset
    })

  if (error) throw error
  return normalizeCampaignPerformanceRows(data)
}

// ------------------------------------------------------------------------------
// CROSS-WORKSPACE FUNCTIONS
// ------------------------------------------------------------------------------

export async function getLeadsFromAllWorkspaces(
  limit = 50,
  offset = 0
): Promise<CRMLead[]> {
  const { data, error } = await getClient()
    .from("crm_contacts")
    .select("*")
    .order("created_at", { ascending: false })
    .range(offset, offset + limit - 1)

  if (error) throw error
  return data as CRMLead[]
}

export async function getCompaniesFromAllWorkspaces(
  limit = 50,
  offset = 0
): Promise<CRMCompany[]> {
  const { data, error } = await getClient()
    .from("crm_companies")
    .select("*")
    .order("created_at", { ascending: false })
    .range(offset, offset + limit - 1)

  if (error) throw error
  return data as CRMCompany[]
}

export async function getContactsFromAllWorkspaces(
  limit = 50,
  offset = 0
): Promise<CRMContact[]> {
  const { data, error } = await getClient()
    .from("crm_contacts")
    .select("*")
    .order("created_at", { ascending: false })
    .range(offset, offset + limit - 1)

  if (error) throw error
  return data as CRMContact[]
}

export async function getContactsWithCount(
  limit = 50,
  offset = 0,
  filters: {
    typeFilter?: string
    statusFilter?: string
    groupFilter?: string
    workspaceId?: string
    sortBy?: string
    sortOrder?: 'asc' | 'desc'
  } = {}
): Promise<{ data: CRMContact[]; total: number }> {
  const client = getClient()
  let query = client
    .from("crm_contacts")
    .select("*", { count: "exact" })

  // Apply filters
  if (filters.typeFilter && filters.typeFilter !== "all") {
    query = query.eq("type", filters.typeFilter)
  }

  if (filters.statusFilter && filters.statusFilter !== "all") {
    query = query.eq("status", filters.statusFilter)
  }

  if (filters.groupFilter && filters.groupFilter !== "all") {
    if (filters.groupFilter === "none") {
      query = query.or("group_name.is.null,group_name.eq.''")
    } else {
      query = query.eq("group_name", filters.groupFilter)
    }
  }

  if (filters.workspaceId) {
    query = query.eq("workspace_id", filters.workspaceId)
  }

  // Apply sorting
  const sortBy = filters.sortBy || "created_at"
  const sortOrder = filters.sortOrder || "desc"

  let orderColumn = "created_at"
  let ascending = sortOrder === "asc"

  // Map sort options to actual column names
  switch (sortBy) {
    case "name":
      orderColumn = "first_name"
      break
    case "created_at":
      orderColumn = "created_at"
      break
    case "updated_at":
      orderColumn = "updated_at"
      break
    case "company":
      orderColumn = "company"
      break
    default:
      orderColumn = "created_at"
  }

  const { data, error, count } = await query
    .order(orderColumn, { ascending })
    .range(offset, offset + limit - 1)

  if (error) throw error
  return { data: data as CRMContact[], total: count || 0 }
}

export async function getLeadsWithCount(
  limit = 50,
  offset = 0,
  filters: {
    statusFilter?: string
    ratingFilter?: string
    groupFilter?: string
    workspaceId?: string
  } = {}
): Promise<{ data: CRMLead[]; total: number }> {
  const client = getClient()
  let query = client
    .from("crm_contacts")
    .select("*", { count: "exact" })

  // Apply filters
  if (filters.statusFilter && filters.statusFilter !== "all") {
    query = query.eq("lead_status", filters.statusFilter)
  }

  if (filters.ratingFilter && filters.ratingFilter !== "all") {
    query = query.eq("lead_rating", filters.ratingFilter)
  }

  if (filters.groupFilter && filters.groupFilter !== "all") {
    if (filters.groupFilter === "none") {
      query = query.or("group_name.is.null,group_name.eq.''")
    } else {
      query = query.eq("group_name", filters.groupFilter)
    }
  }

  if (filters.workspaceId) {
    query = query.eq("workspace_id", filters.workspaceId)
  }

  const { data, error, count } = await query
    .order("created_at", { ascending: false })
    .range(offset, offset + limit - 1)

  if (error) throw error
  return { data: data as CRMLead[], total: count || 0 }
}

export async function getOpportunitiesFromAllWorkspaces(
  limit = 50,
  offset = 0
): Promise<(CRMOpportunity & { workspace: { id: string; name: string } })[]> {
  const { data, error } = await getClient()
    .from("crm_opportunities")
    .select(`
      *,
      workspace:workspaces(id, name)
    `)
    .order("created_at", { ascending: false })
    .range(offset, offset + limit - 1)

  if (error) throw error
  return data as (CRMOpportunity & { workspace: { id: string; name: string } })[]
}

// ------------------------------------------------------------------------------
// CRM ASSIGNMENTS AND SCHEDULED ACTIONS
// ------------------------------------------------------------------------------

export async function assignContactToUser(
  contactId: string,
  userId: string,
  assignedBy: string,
  notes?: string,
  priority: CRMPriority = 'medium'
): Promise<CRMContact> {
  const { data, error } = await getClient()
    .from("crm_contacts")
    .update({
      assigned_to: userId,
      assigned_by: assignedBy,
      assigned_at: new Date().toISOString(),
      assignment_status: 'assigned',
      assignment_notes: notes,
      priority
    })
    .eq("id", contactId)
    .select()
    .single()

  if (error) throw error

  // Log the assignment activity
  await logContactAssignment(contactId, userId, assignedBy, notes, priority)

  return data as CRMContact
}

export async function unassignContact(contactId: string): Promise<CRMContact> {
  const { data, error } = await getClient()
    .from("crm_contacts")
    .update({
      assigned_to: null,
      assigned_by: null,
      assigned_at: null,
      assignment_status: 'unassigned',
      assignment_notes: null
    })
    .eq("id", contactId)
    .select()
    .single()

  if (error) throw error
  return data as CRMContact
}

export async function getAssignedContacts(userId: string): Promise<CRMContact[]> {
  const { data, error } = await getClient()
    .from("crm_contacts")
    .select("*")
    .eq("assigned_to", userId)
    .order("assigned_at", { ascending: false })

  if (error) throw error
  return data as CRMContact[]
}

export async function assignLeadToUser(
  leadId: string,
  userId: string
): Promise<CRMLead> {
  const { data, error } = await getClient()
    .from("crm_contacts")
    .update({ assigned_to: userId })
    .eq("id", leadId)
    .select()
    .single()

  if (error) throw error
  return data as CRMLead
}

export async function unassignLead(leadId: string): Promise<CRMLead> {
  const { data, error } = await getClient()
    .from("crm_contacts")
    .update({ assigned_to: null })
    .eq("id", leadId)
    .select()
    .single()

  if (error) throw error
  return data as CRMLead
}

export async function createScheduledAction(
  action: Omit<CRMScheduledAction, "id" | "created_at" | "updated_at">
): Promise<CRMScheduledAction> {
  const { data, error } = await getClient()
    .from("crm_scheduled_actions")
    .insert(action)
    .select()
    .single()

  if (error) throw error

  const scheduledAction = data as CRMScheduledAction

  // Log the scheduled action activity
  await logScheduledAction(action.contact_id, scheduledAction)

  trackBrowserCrmOutcome({
    surface: "nexus",
    outcome_type: "follow_up",
    qualifying_action: "scheduled",
    agent_id: scheduledAction.agent_id ?? undefined,
    contact_id: scheduledAction.contact_id,
    workspace_id: scheduledAction.workspace_id
  })

  return scheduledAction
}

export async function getScheduledActionsForContact(contactId: string): Promise<CRMScheduledAction[]> {
  const { data, error } = await getClient()
    .from("crm_scheduled_actions")
    .select("*")
    .eq("contact_id", contactId)
    .order("scheduled_at", { ascending: true })

  if (error) throw error
  return data as CRMScheduledAction[]
}

export async function getScheduledActionsForAgent(agentId: string): Promise<CRMScheduledAction[]> {
  const { data, error } = await getClient()
    .from("crm_scheduled_actions")
    .select(`
      *,
      crm_contacts!inner(*)
    `)
    .eq("agent_id", agentId)
    .eq("action_status", "scheduled")
    .order("scheduled_at", { ascending: true })

  if (error) throw error
  return data as (CRMScheduledAction & { crm_contacts: CRMContact })[]
}

export async function updateScheduledActionStatus(
  actionId: string,
  status: CRMScheduledActionStatus,
  resultNotes?: string,
  executedAt?: string
): Promise<CRMScheduledAction> {
  const updates: any = {
    action_status: status,
    updated_at: new Date().toISOString()
  }

  if (resultNotes) updates.result_notes = resultNotes
  if (executedAt) updates.executed_at = executedAt
  if (status === 'completed') updates.completed_at = new Date().toISOString()

  const { data, error } = await getClient()
    .from("crm_scheduled_actions")
    .update(updates)
    .eq("id", actionId)
    .select()
    .single()

  if (error) throw error
  return data as CRMScheduledAction
}

export async function getPendingScheduledActions(): Promise<(CRMScheduledAction & { crm_contacts: CRMContact; nexus_agents?: NexusAgent })[]> {
  const now = new Date().toISOString()

  const { data, error } = await getClient()
    .from("crm_scheduled_actions")
    .select(`
      *,
      crm_contacts!inner(*),
      nexus_agents(*)
    `)
    .eq("action_status", "scheduled")
    .lte("scheduled_at", now)
    .order("scheduled_at", { ascending: true })

  if (error) throw error
  return data as unknown as (CRMScheduledAction & { crm_contacts: CRMContact; nexus_agents?: NexusAgent })[]
}

// ------------------------------------------------------------------------------
// ACTIVITY TRACKING
// ------------------------------------------------------------------------------

export async function createCRMActivity(activity: Omit<CRMActivity, "id" | "created_at">): Promise<CRMActivity> {
  // Remove fields that don't exist in crm_contact_activities table
  const { lead_id, opportunity_id, company_id, old_value, new_value, ...contactActivity } = activity
  const insert = contactActivity as unknown as Database["public"]["Tables"]["crm_contact_activities"]["Insert"]

  const { data, error } = await getClient()
    .from("crm_contact_activities")
    .insert(insert)
    .select()
    .single()

  if (error) throw error
  return data as unknown as CRMActivity
}

export async function getContactActivities(contactId: string, limit = 50): Promise<CRMActivity[]> {
  // BUG-06: the per-contact timeline must reflect one scoped source of
  // truth. Historically two tables exist: `crm_contact_activities` (the
  // scoped table this view used) and the global `crm_activities` table
  // where outbound/AI emails, calls, and webhook events are written with a
  // `contact_id`. Reading only the scoped table hid all email/AI activity.
  // We now union both tables filtered by contact_id, dedupe, and sort.
  const client = getClient()

  const [contactActivitiesResult, globalActivitiesResult] = await Promise.all([
    client
      .from('crm_contact_activities')
      .select('*')
      .eq('contact_id', contactId)
      .order('created_at', { ascending: false })
      .limit(limit),
    client
      .from('crm_activities')
      .select('*')
      .eq('contact_id', contactId)
      .order('created_at', { ascending: false })
      .limit(limit)
  ])

  if (contactActivitiesResult.error) throw contactActivitiesResult.error
  // The global table is best-effort: never fail the timeline if it errors.
  if (globalActivitiesResult.error) {
    safeConsole.warn(
      'Failed to fetch global crm_activities for contact timeline:',
      globalActivitiesResult.error
    )
  }

  const scopedRows = (contactActivitiesResult.data ??
    []) as unknown as CRMActivitySourceRow[]
  const globalRows = ((globalActivitiesResult.data ?? []) as CRMJsonRecord[]).map(
    (row): CRMActivitySourceRow => ({
      ...row,
      // Normalize global rows to the scoped shape used by the timeline.
      activity_type:
        (row.activity_type as string | undefined) ?? (row.type as string | undefined) ?? 'note',
      performed_by:
        (row.performed_by as string | undefined) ??
        (row.assigned_to as string | undefined) ??
        null,
      performed_by_type:
        (row.performed_by_type as string | undefined) ??
        (row.ai_generated ? 'ai_agent' : row.assigned_to ? 'user' : null)
    })
  )

  // Dedupe by id (a row could appear in both tables after a backfill).
  const byId = new Map<string, CRMActivitySourceRow>()
  for (const row of [...scopedRows, ...globalRows]) {
    const id = typeof row.id === 'string' ? row.id : undefined
    if (id && !byId.has(id)) {
      byId.set(id, row)
    }
  }

  const activityRows = Array.from(byId.values())
    .sort((a, b) => {
      const at = typeof a.created_at === 'string' ? new Date(a.created_at).getTime() : 0
      const bt = typeof b.created_at === 'string' ? new Date(b.created_at).getTime() : 0
      return bt - at
    })
    .slice(0, limit)

  if (activityRows.length === 0) return []

  // Get unique performer user IDs (excluding AI agents)
  const userIds = getActivityPerformerUserIds(activityRows)

  // Get profiles for these users
  let profilesData: any[] = []
  if (userIds.length > 0) {
    const { data: profiles, error: profilesError } = await client
      .from('profiles')
      .select('user_id, username, display_name, image_url')
      .in('user_id', userIds)

    if (profilesError) {
      safeConsole.warn('Failed to fetch profiles for activities:', profilesError)
    } else {
      profilesData = profiles || []
    }
  }

  // Combine activities with profile data
  return withActivityProfiles(activityRows, profilesData)
}

export async function logContactAssignment(contactId: string, assignedTo: string, assignedBy: string, notes?: string, priority?: CRMPriority): Promise<void> {
  const contact = await getContactById(contactId)
  if (!contact) throw new Error('Contact not found')

  await createCRMActivity({
    workspace_id: contact.workspace_id,
    activity_type: 'assignment',
    title: `Contact assigned to team member`,
    description: notes || `Assigned with ${priority || 'medium'} priority`,
    contact_id: contactId,
    performed_by: assignedBy,
    performed_by_type: 'user',
    status: 'completed',
    type: 'assignment',
    updated_at: new Date().toISOString(),
    ai_generated: false,
    new_value: {
      assigned_to: assignedTo,
      priority: priority || 'medium',
      notes: notes
    }
  })
}

export async function logScheduledAction(contactId: string, action: CRMScheduledAction): Promise<void> {
  const contact = await getContactById(contactId)
  if (!contact) throw new Error('Contact not found')

  await createCRMActivity({
    workspace_id: contact.workspace_id,
    activity_type: 'scheduled_action',
    title: `AI action scheduled: ${action.action_type}`,
    description: `Scheduled for ${new Date(action.scheduled_at).toLocaleString()}`,
    contact_id: contactId,
    performed_by: action.assigned_by,
    performed_by_type: 'user',
    agent_id: action.agent_id,
    scheduled_action_id: action.id,
    status: 'completed',
    type: 'scheduled_action',
    updated_at: new Date().toISOString(),
    ai_generated: false,
    metadata: {
      action_type: action.action_type,
      scheduled_at: action.scheduled_at,
      has_subject: Boolean(action.subject)
    }
  })
}

export async function logManualAction(contactId: string, actionType: CRMActivityType, title: string, description?: string, performedBy?: string): Promise<void> {
  const contact = await getContactById(contactId)
  if (!contact) throw new Error('Contact not found')

  await createCRMActivity({
    workspace_id: contact.workspace_id,
    activity_type: actionType,
    title,
    description,
    contact_id: contactId,
    performed_by: performedBy,
    performed_by_type: 'user',
    status: 'completed',
    type: actionType,
    updated_at: new Date().toISOString(),
    ai_generated: false
  })

  // Update contact status and last_contacted_at based on activity type
  await updateContactStatusOnActivity(contactId, actionType, 'manual')
}

export async function logAIActions(scheduledAction: CRMScheduledAction, results: { success: boolean; notes: string }): Promise<void> {
  const contact = await getContactById(scheduledAction.contact_id)
  if (!contact) throw new Error('Contact not found')

  const activityType = scheduledAction.action_type === 'call' ? 'ai_call' :
                      scheduledAction.action_type === 'email' ? 'ai_email' :
                      scheduledAction.action_type === 'sms' ? 'ai_sms' : 'ai_meeting'

  await createCRMActivity({
    workspace_id: contact.workspace_id,
    activity_type: activityType as CRMActivityType,
    title: `AI ${scheduledAction.action_type} ${results.success ? 'completed' : 'failed'}`,
    description: results.notes,
    contact_id: scheduledAction.contact_id,
    performed_by_type: 'ai_agent',
    agent_id: scheduledAction.agent_id,
    scheduled_action_id: scheduledAction.id,
    status: results.success ? 'completed' : 'cancelled',
    type: activityType as CRMActivityType,
    updated_at: new Date().toISOString(),
    ai_generated: true,
    metadata: {
      success: results.success,
      action_type: scheduledAction.action_type,
      scheduled_at: scheduledAction.scheduled_at,
      executed_at: scheduledAction.executed_at
    }
  })

  // Update contact status and last_contacted_at based on activity type
  if (results.success) {
    await updateContactStatusOnActivity(scheduledAction.contact_id, activityType as CRMActivityType, 'ai')
  }
}

// Helper function to update contact status based on activity type
async function updateContactStatusOnActivity(contactId: string, activityType: CRMActivityType, activitySource: 'manual' | 'ai'): Promise<void> {
  try {
    const client = getClient()

    // Define which activities count as "contacted"
    const contactActivities = [
      'call', 'email', 'sms', 'meeting',
      'manual_call', 'manual_email', 'manual_sms', 'manual_meeting',
      'ai_call', 'ai_email', 'ai_sms', 'ai_meeting',
      'linkedin_message', 'ai_outreach'
    ]

    if (!contactActivities.includes(activityType)) {
      // For non-contact activities, just update last_contacted_at
      const { error } = await client
        .from("crm_contacts")
        .update({
          last_contacted_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq("id", contactId)

      if (error) {
        safeConsole.error('Error updating last_contacted_at:', error)
      }
      return
    }

    // For contact activities, update status to 'contacted' if it's currently 'new'
    const { data: contact } = await client
      .from("crm_contacts")
      .select("status")
      .eq("id", contactId)
      .single()

    if (contact) {
      let newStatus: CRMContactStatus = (contact as any).status || 'new'

      // If status is 'new', change to 'contacted' on first contact activity
      if (!newStatus || newStatus === 'new') {
        newStatus = 'contacted'
      }

      const { error: updateError } = await client
        .from("crm_contacts")
        .update({
          status: newStatus,
          last_contacted_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq("id", contactId)

      if (updateError) {
        safeConsole.error('Error updating contact status:', updateError)
      }
    }
  } catch (error) {
    safeConsole.error('Error updating contact status on activity:', error)
    // Don't throw - this shouldn't break the activity logging
  }
}

// ------------------------------------------------------------------------------
// OUTBOUND MESSAGING
// ------------------------------------------------------------------------------

export async function createOutboundMessage(
  message: Omit<CRMOutboundMessage, "id" | "created_at" | "updated_at">,
  supabaseClient?: SupabaseClient<Database>
): Promise<CRMOutboundMessage> {
  const client = getClient(supabaseClient)
  const { data, error } = await client
    .from("crm_outbound_messages")
    .insert(message)
    .select()
    .single()

  if (error) throw error
  return data as CRMOutboundMessage
}

export async function getOutboundMessagesByAgent(
  agentId: string,
  limit = 50,
  offset = 0
): Promise<CRMOutboundMessage[]> {
  const { data, error } = await getClient()
    .from("crm_outbound_messages")
    .select("*")
    .eq("agent_id", agentId)
    .order("created_at", { ascending: false })
    .range(offset, offset + limit - 1)

  if (error) throw error
  return data as CRMOutboundMessage[]
}

export async function updateOutboundMessage(
  messageId: string,
  updates: Partial<CRMOutboundMessage>
): Promise<CRMOutboundMessage> {
  const { data, error } = await getClient()
    .from("crm_outbound_messages")
    .update(updates)
    .eq("id", messageId)
    .select()
    .single()

  if (error) throw error
  return data as CRMOutboundMessage
}

// ------------------------------------------------------------------------------
// CRM AGENT CONFIGURATION
// ------------------------------------------------------------------------------

export async function createAgentConfig(
  config: Omit<CRMAgentConfig, "id" | "created_at" | "updated_at">
): Promise<CRMAgentConfig> {
  const { data, error } = await getClient()
    .from("crm_ai_agent_configs")
    .insert(config)
    .select()
    .single()

  if (error) throw error
  return data as CRMAgentConfig
}

export async function getAgentConfig(agentId: string): Promise<CRMAgentConfig | null> {
  const { data, error } = await getClient()
    .from("crm_ai_agent_configs")
    .select("*")
    .eq("agent_id", agentId)
    .single()

  if (error && error.code !== 'PGRST116') throw error
  return data as CRMAgentConfig | null
}

export async function updateAgentConfig(
  agentId: string,
  updates: Partial<CRMAgentConfig>
): Promise<CRMAgentConfig> {
  const { data, error } = await getClient()
    .from("crm_ai_agent_configs")
    .update(updates)
    .eq("agent_id", agentId)
    .select()
    .single()

  if (error) throw error
  return data as CRMAgentConfig
}

export async function deleteAgentConfig(agentId: string): Promise<void> {
  const { error } = await getClient()
    .from("crm_ai_agent_configs")
    .delete()
    .eq("agent_id", agentId)

  if (error) throw error
}

// ------------------------------------------------------------------------------
// UTILITIES
// ------------------------------------------------------------------------------

export async function getCRMStatsFromAllWorkspaces(
  supabaseClient?: SupabaseClient<Database>
): Promise<{
  companies: number
  contacts: number
  leads: number
  opportunities: number
  activities: number
  campaigns: number
  forms: number
}> {
  // Use provided client or default to browser client
  const client = getClient(supabaseClient)

  safeConsole.log('Getting CRM stats from all workspaces')

  try {
    // Get total counts from all accessible workspaces (RLS will filter)
    const queries = [
      client.from("crm_companies").select("*", { count: "exact", head: true }),
      client.from("crm_contacts").select("*", { count: "exact", head: true }),
      client.from("crm_contacts").select("*", { count: "exact", head: true }),
      client.from("crm_opportunities").select("*", { count: "exact", head: true }),
      client.from("crm_activities").select("*", { count: "exact", head: true }),
      client.from("crm_campaigns").select("*", { count: "exact", head: true }),
      client.from("crm_forms").select("*", { count: "exact", head: true })
    ]

    const results = await Promise.allSettled(queries)

    // Extract counts and handle any errors
    const counts = results.map((result, index) => {
      if (result.status === 'fulfilled') {
        const count = result.value.count
        safeConsole.log(`Query ${index} count:`, count)
        return count || 0
      } else {
        safeConsole.error(`Query ${index} error:`, result.reason)
        return 0
      }
    })

    const stats = {
      companies: counts[0],
      contacts: counts[1],
      leads: counts[2],
      opportunities: counts[3],
      activities: counts[4],
      campaigns: counts[5],
      forms: counts[6]
    }

    safeConsole.log('Final CRM stats from all workspaces:', stats)
    return stats
  } catch (error) {
    safeConsole.error('Error getting CRM stats from all workspaces:', error)
    return {
      companies: 0,
      contacts: 0,
      leads: 0,
      opportunities: 0,
      activities: 0,
      campaigns: 0,
      forms: 0
    }
  }
}

/** Allowlisted CRM table keys for safe count/query (no raw SQL from LLM). */
const CRM_TABLE_MAP: Record<string, string> = {
  leads: "crm_contacts",
  contacts: "crm_contacts",
  companies: "crm_companies",
  activities: "crm_activities",
  opportunities: "crm_opportunities",
  campaigns: "crm_campaigns"
}

/** Allowlisted filter fields and values to prevent SQL injection. */
const CRM_LEAD_STATUSES = new Set(["new", "contacted", "qualified", "propose", "negotiation", "closed_won", "closed_lost", "recycled"])
const CRM_LEAD_RATINGS = new Set(["hot", "warm", "cold", "unrated"])
const CRM_ACTIVITY_TYPES = new Set([
  "call", "email", "sms", "meeting", "task", "note", "demo", "proposal", "follow_up",
  "research", "linkedin_message", "ai_outreach", "ai_call", "ai_email", "assignment",
  "scheduled_action", "manual_call", "manual_email", "manual_sms", "manual_meeting",
  "ai_sms", "ai_meeting", "status_change", "priority_change"
])

/**
 * Safe count for one CRM table with optional filter. Used by Nexus LLM tools.
 * Table and filter field/value are allowlisted; no user-supplied SQL.
 */
export async function getCRMCount(
  workspaceId: string,
  tableKey: string,
  filter: { field: string; value: string } | null,
  supabaseClient?: SupabaseClient<Database>
): Promise<number> {
  const tableName = CRM_TABLE_MAP[tableKey]
  if (!tableName) return 0
  const client = getClient(supabaseClient) as unknown as CRMDynamicTableClient
  let q = client.from(tableName).select("*", { count: "exact", head: true }).eq("workspace_id", workspaceId)
  if (filter) {
    if (tableKey === "leads" && filter.field === "status" && CRM_LEAD_STATUSES.has(filter.value)) {
      q = q.eq("status", filter.value)
    } else if (tableKey === "leads" && filter.field === "rating" && CRM_LEAD_RATINGS.has(filter.value)) {
      q = q.eq("rating", filter.value)
    } else if (tableKey === "activities" && filter.field === "type" && CRM_ACTIVITY_TYPES.has(filter.value)) {
      q = q.eq("type", filter.value)
    }
  }
  const { count, error } = await q
  if (error) throw error
  return count ?? 0
}

/**
 * Safe grouped count for CRM tables. Used by Nexus LLM tools (e.g. leads by status).
 * Group-by field is allowlisted; no user-supplied SQL.
 */
export async function getCRMCountGrouped(
  workspaceId: string,
  tableKey: string,
  groupByField: "status" | "rating" | "type",
  supabaseClient?: SupabaseClient<Database>
): Promise<{ value: string; count: number }[]> {
  const tableName = CRM_TABLE_MAP[tableKey]
  if (!tableName) return []
  const client = getClient(supabaseClient) as unknown as CRMDynamicTableClient
  const allowed =
    (tableKey === "leads" && (groupByField === "status" || groupByField === "rating")) ||
    (tableKey === "activities" && groupByField === "type")
  if (!allowed) return []
  const { data, error } = await client
    .from(tableName)
    .select(groupByField)
    .eq("workspace_id", workspaceId)
  if (error) throw error
  const counts: Record<string, number> = {}
  for (const row of data ?? []) {
    const v = row[groupByField]
    const key = typeof v === "string" && v.length > 0 ? v : "unknown"
    counts[key] = (counts[key] ?? 0) + 1
  }
  return Object.entries(counts).map(([value, count]) => ({ value, count }))
}

export async function getCRMStats(
  workspaceId: string,
  supabaseClient?: SupabaseClient<Database>
): Promise<CRMStats> {
  // Use provided client or default to browser client
  const client = getClient(supabaseClient)

  safeConsole.log('Getting CRM stats for workspace:', workspaceId)

  const results = await Promise.all(
    CRM_STATS_TABLES.map(({ table }) =>
      client
        .from(table)
        .select("*", { count: "exact", head: true })
        .eq("workspace_id", workspaceId)
    )
  )

  const stats = results.reduce<CRMStats>((nextStats, result, index) => {
    if (result.error) {
      safeConsole.error(
        `CRM stats query failed for ${CRM_STATS_TABLES[index].table}:`,
        result.error
      )
      throw result.error
    }

    nextStats[CRM_STATS_TABLES[index].key] = result.count ?? 0
    return nextStats
  }, createEmptyCRMStats())

  safeConsole.log('Final CRM stats:', stats)
  return stats
}

// ------------------------------------------------------------------------------
// CRM INTEGRATIONS
// ------------------------------------------------------------------------------

export async function getCRMIntegration(
  workspaceId: string,
  provider: string,
  supabaseClient?: SupabaseClient<Database>
): Promise<any | null> {
  const client = getClient(supabaseClient)

  try {
    const { data, error } = await client
      .from('crm_integrations')
      .select('*')
      .eq('workspace_id', workspaceId)
      .eq('provider', provider)
      .eq('sync_status', 'active')
      .single()

    return error ? null : data
  } catch {
    return null
  }
}

export async function getCRMIntegrationsByWorkspace(
  workspaceId: string,
  supabaseClient?: SupabaseClient<Database>
): Promise<any[]> {
  const client = getClient(supabaseClient)

  const { data, error } = await client
    .from('crm_integrations')
    .select('*')
    .eq('workspace_id', workspaceId)
    .order('created_at', { ascending: false })

  if (error) throw error
  return data ? data as unknown[] : []
}

// ------------------------------------------------------------------------------
// ACTIVITY QUERY FUNCTIONS
// ------------------------------------------------------------------------------

export async function getActivitiesByLead(leadId: string, supabaseClient?: SupabaseClient): Promise<CRMActivity[]> {
  const client = getClient(supabaseClient)

  const { data: activities, error } = await client
    .from('crm_activities')
    .select('*')
    .eq('contact_id', leadId)
    .order('created_at', { ascending: false })

  if (error) throw error
  if (!activities || activities.length === 0) return []
  const activityRows = activities as unknown as CRMActivitySourceRow[]

  // Get unique performer user IDs (excluding AI agents)
  const userIds = getActivityPerformerUserIds(activityRows)

  // Get profiles for these users
  let profilesData: any[] = []
  if (userIds.length > 0) {
    const { data: profiles, error: profilesError } = await client
      .from('profiles')
      .select('user_id, username, display_name, image_url')
      .in('user_id', userIds)

    if (profilesError) {
      safeConsole.warn('Failed to fetch profiles for activities:', profilesError)
    } else {
      profilesData = profiles || []
    }
  }

  // Combine activities with profile data
  return withActivityProfiles(activityRows, profilesData)
}

// ------------------------------------------------------------------------------
// Conversation Search Functions
// ------------------------------------------------------------------------------

export async function getActivitiesByContact(contactId: string, supabaseClient?: SupabaseClient): Promise<CRMActivity[]> {
  const client = getClient(supabaseClient)

  // Since crm_leads merged into crm_contacts, query activities directly by contact_id
  const { data: activities, error } = await client
    .from('crm_activities')
    .select('*')
    .eq('contact_id', contactId)
    .order('created_at', { ascending: false })

  if (error) throw error
  if (!activities || activities.length === 0) return []
  const activityRows = activities as unknown as CRMActivitySourceRow[]

  // Get unique performer user IDs (excluding AI agents)
  const userIds = getActivityPerformerUserIds(activityRows)

  // Get profiles for these users
  let profilesData: any[] = []
  if (userIds.length > 0) {
    const { data: profiles, error: profilesError } = await client
      .from('profiles')
      .select('user_id, username, display_name, image_url')
      .in('user_id', userIds)

    if (profilesError) {
      safeConsole.warn('Failed to fetch profiles for activities:', profilesError)
    } else {
      profilesData = profiles || []
    }
  }

  // Combine activities with profile data
  return withActivityProfiles(activityRows, profilesData)
}

// ------------------------------------------------------------------------------
// Conversation Search Functions
// ------------------------------------------------------------------------------

export async function getContactActivitiesByWorkspace(workspaceId: string, limit?: number): Promise<CRMActivity[]> {
  // First get the activities with contact info
  const { data: activities, error } = await getClient()
    .from('crm_contact_activities')
    .select(`
      *,
      contact:crm_contacts!crm_contact_activities_contact_id_fkey (
        id,
        first_name,
        last_name,
        workspace_id
      )
    `)
    .eq('contact.workspace_id', workspaceId)
    .order('created_at', { ascending: false })
    .limit(limit || 10)

  if (error) throw error
  if (!activities || activities.length === 0) return []
  const activityRows = activities as unknown as CRMActivitySourceRow[]

  // Get unique performer user IDs (excluding AI agents)
  const userIds = getActivityPerformerUserIds(activityRows)

  // Get profiles for these users
  let profilesData: any[] = []
  if (userIds.length > 0) {
    const { data: profiles, error: profilesError } = await getClient()
      .from('profiles')
      .select('user_id, username, display_name, image_url')
      .in('user_id', userIds)

    if (profilesError) {
      safeConsole.warn('Failed to fetch profiles for activities:', profilesError)
    } else {
      profilesData = profiles || []
    }
  }

  // Combine activities with profile data
  return withActivityProfiles(activityRows, profilesData)
}

// ------------------------------------------------------------------------------
// Conversation Search Functions
// ------------------------------------------------------------------------------

export async function getActivitiesByWorkspace(workspaceId: string, limit?: number, supabaseClient?: SupabaseClient): Promise<CRMActivity[]> {
  const client = getClient(supabaseClient)

  let query = client
    .from('crm_activities')
    .select('*')
    .eq('workspace_id', workspaceId)
    .order('created_at', { ascending: false })

  if (limit) {
    query = query.limit(limit)
  }

  const { data: activities, error } = await query

  if (error) throw error
  if (!activities || activities.length === 0) return []
  const activityRows = activities as unknown as CRMActivitySourceRow[]

  // Get unique performer user IDs (excluding AI agents)
  const userIds = getActivityPerformerUserIds(activityRows)

  // Get profiles for these users
  let profilesData: any[] = []
  if (userIds.length > 0) {
    const { data: profiles, error: profilesError } = await client
      .from('profiles')
      .select('user_id, username, display_name, image_url')
      .in('user_id', userIds)

    if (profilesError) {
      safeConsole.warn('Failed to fetch profiles for activities:', profilesError)
    } else {
      profilesData = profiles || []
    }
  }

  // Combine activities with profile data
  return withActivityProfiles(activityRows, profilesData)
}

// ------------------------------------------------------------------------------
// Conversation Search Functions
// ------------------------------------------------------------------------------

export async function searchConversationsByLead(workspaceId: string, leadId: string, agentId?: string): Promise<any[]> {
  safeConsole.log('Searching conversations for workspace:', workspaceId, 'lead:', leadId)

  // First try searching by metadata->>lead_id (for outbound calls)
  let { data, error } = await getClient()
    .from('nexus_conversations')
    .select(`
      *,
      nexus_agents!inner(
        id,
        name,
        workspace_id
      )
    `)
    .eq('nexus_agents.workspace_id', workspaceId)
    .eq('metadata->>lead_id', leadId)
    .order('started_at', { ascending: false })
    .limit(10)

  if (error) {
    safeConsole.error('Error searching conversations by metadata lead_id:', error)
  }

  safeConsole.log('Conversations found by metadata lead_id:', data?.length || 0)

  // If no conversations found, try searching by crm_contact_id (for inbound calls)
  if (!data || data.length === 0) {
    const { data: contactData, error: contactError } = await getClient()
      .from('nexus_conversations')
      .select(`
        *,
        nexus_agents!inner(
          id,
          name,
          workspace_id
        )
      `)
      .eq('nexus_agents.workspace_id', workspaceId)
      .eq('crm_contact_id', leadId)
      .order('started_at', { ascending: false })
      .limit(10)

    if (contactError) {
      safeConsole.error('Error searching conversations by crm_contact_id:', contactError)
    } else {
      safeConsole.log('Conversations found by crm_contact_id:', contactData?.length || 0)
      data = contactData || []
    }
  }

  // If still no conversations, try searching by agent_id if provided
  if ((!data || data.length === 0) && agentId) {
    safeConsole.log('No conversations found by lead_id, trying agent search for agent:', agentId)
    const { data: agentData, error: agentError } = await getClient()
      .from('nexus_conversations')
      .select(`
        *,
        nexus_agents!inner(
          id,
          name,
          workspace_id
        )
      `)
      .eq('nexus_agents.workspace_id', workspaceId)
      .eq('agent_id', agentId)
      .order('started_at', { ascending: false })
      .limit(5)

    if (agentError) {
      safeConsole.error('Error searching conversations by agent:', agentError)
    } else {
      safeConsole.log('Conversations found by agent search:', agentData?.length || 0)
      data = agentData || []
    }
  }

  // Final fallback: search for recent conversations in this workspace
  if (!data || data.length === 0) {
    safeConsole.log('No conversations found, trying recent workspace conversations...')
    const { data: recentData, error: recentError } = await getClient()
      .from('nexus_conversations')
      .select(`
        *,
        nexus_agents!inner(
          id,
          name,
          workspace_id
        )
      `)
      .eq('nexus_agents.workspace_id', workspaceId)
      .order('started_at', { ascending: false })
      .limit(10)

    if (recentError) {
      safeConsole.error('Error searching recent conversations:', recentError)
    } else {
      safeConsole.log('Recent conversations found:', recentData?.length || 0)
      // Filter by conversations that might be related to this lead
      // This is a last resort fallback
      data = recentData?.filter(conv => {
        const metadata = conv.metadata as { lead_id?: unknown } | null

        return (
          metadata?.lead_id === leadId ||
          conv.crm_contact_id === leadId ||
          conv.external_id?.includes(leadId.substring(0, 8))
        )
      }) || []
      safeConsole.log('Filtered conversations by lead:', data.length)
    }
  }

  return data || []
}
